import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Trash2 } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const MaterialPickerModal = ({
  materials,
  alreadyAddedIds,
  onConfirm,
  onClose,
  onRedirectToMaterialLibrary,
}) => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);

  const filtered = materials.filter((m) => {
    const q = search.toLowerCase();
    return (
      m.material_name?.toLowerCase().includes(q) ||
      m.subcategory?.toLowerCase().includes(q) ||
      m.specification?.toLowerCase().includes(q) ||
      m.brand_name?.toLowerCase().includes(q)
    );
  });

  const toggle = (material) => {
    setSelected((prev) =>
      prev.find((m) => m.id === material.id)
        ? prev.filter((m) => m.id !== material.id)
        : [...prev, material],
    );
  };

  const isDisabled = (m) => alreadyAddedIds.includes(m.id);
  const isChecked = (m) => selected.some((s) => s.id === m.id);

  const modalContent = (
    <>
      {materials.length === 0 ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl border border-gray-200 flex flex-col">
            <div className="flex-1 overflow-y-auto px-6 py-10">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#EDE3F6] text-[#3B0069] mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 7h16" />
                  <path d="M6 7l1 12h10l1-12" />
                  <path d="M9 11v5" />
                  <path d="M15 11v5" />
                </svg>
              </div>
              <h3 className="text-center text-lg font-semibold text-[#232323]">
                You have already added all your materials
              </h3>
              <p className="mt-2 text-center text-sm text-gray-500 max-w-sm mx-auto">
                Open your Material Library to add new items before returning to the warehouse.
              </p>
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onRedirectToMaterialLibrary?.();
                  }}
                  className="inline-flex items-center justify-center rounded-lg bg-[#3B0069] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#3B0069D9] transition-colors"
                >
                  Go to Material Library
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl border border-gray-200 flex flex-col">
            <div className="relative flex items-center justify-center px-4 h-16 py-2 border-b border-gray-100 shrink-0">
              <h3 className="text-xl font-medium text-[#232323]">Select Material</h3>
              <button
                onClick={onClose}
                className="absolute right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-4 py-6 shrink-0">
              <span className="block text-sm font-medium text-[#232323] mb-2">
                Select from your material inventory
              </span>
              <div className="relative w-full">
                <input
                  autoFocus
                  type="text"
                  placeholder="Search your material"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-[#F3F4F6] rounded-xl border-0 text-[#232323] focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">
              <div className="space-y-1">
                {filtered.length === 0 && (
                  <p className="text-sm text-gray-500 p-4">
                    No materials found.
                  </p>
                )}
                {filtered.map((material) => {
                  const disabled = isDisabled(material);
                  const checked = isChecked(material);

                  return (
                    <button
                      key={material.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => !disabled && toggle(material)}
                      className={`w-full flex items-center gap-3 px-2 py-1 border-b-[0.5px] border-[#C3C3C3] text-left transition
                        ${disabled ? "cursor-not-allowed opacity-70" : checked ? "bg-purple-50" : "hover:bg-gray-50 cursor-pointer"}`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                          ${disabled ? "bg-gray-300 border-gray-300" : checked ? "bg-[#3B0069] border-[#3B0069]" : "border-[#232323]"}`}
                      >
                        {(checked || disabled) && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="white"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="font-medium text-[#232323]">
                          {material.material_name}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-auto">
                        {material?.specification && (
                          <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                            {material.specification}
                          </span>
                        )}
                        {material?.subcategory && (
                          <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                            {material.subcategory}
                          </span>
                        )}
                        {material?.brand_name && (
                          <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                            {material.brand_name}
                          </span>
                        )}
                        {material?.uom && (
                          <span className="text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                            {material.uom}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pt-2 px-4 border-t border-gray-100 flex items-center justify-between pb-3 shrink-0">
              <span className="text-xs text-gray-500">
                {selected.length > 0
                  ? `${selected.length} material${selected.length > 1 ? "s" : ""} selected`
                  : "Select one or more materials to add"}
              </span>
              <button
                type="button"
                disabled={selected.length === 0}
                onClick={() => onConfirm(selected)}
                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-[#3B0069] disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-[#3B0069D9] transition-colors"
              >
                Add Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return createPortal(modalContent, document.body);
};
const CreateWarehouseMaterial = ({
  onClose,
  onSuccess,
  onRedirectToMaterialLibrary,
}) => {
  const [materials, setMaterials] = useState([]);
  const [warehouseMaterialIds, setWarehouseMaterialIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useRef(null);
  const { addAlert } = useAlert();

  const [organisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id,
  );

  const [lines, setLines] = useState([]);

  const getMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const [libraryResponse, warehouseResponse] = await Promise.allSettled([
        fetch(`${BASE_URL}/org/material-library/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ org_id: organisationId }),
        }),
        fetch(`${BASE_URL}/org/warehouse/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ org_id: organisationId }),
        }),
      ]);

      if (libraryResponse.status !== "fulfilled" || !libraryResponse.value.ok) {
        throw new Error("Failed to fetch materials");
      }

      const libraryData = await libraryResponse.value.json();
      setMaterials(Array.isArray(libraryData) ? libraryData : []);

      if (
        warehouseResponse.status === "fulfilled" &&
        warehouseResponse.value.ok
      ) {
        const warehouseData = await warehouseResponse.value.json();
        const existingIds = Array.isArray(warehouseData)
          ? [
              ...new Set(
                warehouseData
                  .map((item) => item?.material?.id)
                  .filter((id) => id !== null && id !== undefined),
              ),
            ]
          : [];
        setWarehouseMaterialIds(existingIds);
      } else {
        setWarehouseMaterialIds([]);
        console.error("Failed to fetch warehouse materials for filtering.");
      }
    } catch (err) {
      console.error("Error fetching materials:", err);
      addAlert("Failed to load materials", "error");
    } finally {
      setLoading(false);
    }
  }, [addAlert, organisationId]);

  useEffect(() => {
    getMaterials();
  }, [getMaterials]);

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => setIsMounted(true));
    return () => {
      window.cancelAnimationFrame(raf);
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const requestClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    setIsMounted(false);
    closeTimerRef.current = window.setTimeout(() => {
      onClose?.();
    }, 260);
  };

  const handlePickerConfirm = (picked) => {
    const newLines = picked.map((m) => ({ material: m, currentQuantity: "" }));
    setLines((prev) => [...prev, ...newLines]);
    setShowPicker(false);
  };

  const removeLine = (index) =>
    setLines((prev) => prev.filter((_, i) => i !== index));

  const updateQty = (index, value) =>
    setLines((prev) =>
      prev.map((line, i) =>
        i === index ? { ...line, currentQuantity: value } : line,
      ),
    );

  const addedIds = lines.map((l) => l.material.id);
  const selectableMaterials = useMemo(() => {
    const existingIdsSet = new Set(warehouseMaterialIds);
    return materials.filter((material) => !existingIdsSet.has(material.id));
  }, [materials, warehouseMaterialIds]);
  const hasSelectableMaterials = selectableMaterials.length > 0;

  const createWarehouseMaterials = async () => {
    if (lines.length === 0) {
      addAlert("Please add at least one material", "error");
      return;
    }
    if (lines.some((l) => !l.currentQuantity)) {
      addAlert("Please enter quantity for all materials", "error");
      return;
    }

    try {
      setSubmitting(true);
      const results = await Promise.all(
        lines.map((line) =>
          fetch(`${BASE_URL}/org/warehouse/create/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
            body: JSON.stringify({
              organization: organisationId,
              material: line.material.id,
              current_quantity: line.currentQuantity,
            }),
          }).then(async (res) => {
            if (!res.ok) {
              const err = await res.json();
              throw new Error(
                err.error || `Failed to add ${line.material.material_name}`,
              );
            }
            return res.json();
          }),
        ),
      );
      addAlert(
        `${results.length} material${results.length > 1 ? "s" : ""} added to warehouse successfully`,
        "success",
      );
      onSuccess?.(results);
      onClose();
    } catch (err) {
      addAlert(err.message || "Failed to create warehouse material", "error");
      console.error("Error creating warehouse material:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex items-stretch justify-end bg-black/50 transition-opacity duration-300 ${isMounted && !isClosing ? "opacity-100" : "opacity-0"}`}
        onClick={requestClose}
      >
        <div
          className={`bg-white w-full sm:max-w-2xl h-full flex flex-col shadow-2xl rounded-l-xl overflow-hidden transform transition-all duration-300 ease-out ${isMounted && !isClosing ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative flex items-center justify-center px-4 h-16 py-2 border-b border-gray-100 sticky top-0 bg-white z-10">
            <h2 className="text-xl font-medium text-[#232323]">
              Add Material to Warehouse
            </h2>
            <button
              onClick={requestClose}
              className="absolute right-4 w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 pb-6">
            {/* <p className="text-sm text-gray-500 mb-5">
              Can't find your material?{" "}
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  onRedirectToMaterialLibrary?.();
                }}
                className="text-[#3B0069] underline font-medium"
              >
                Add here
              </button>
            </p> */}

            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#232323]">
                  Materials
                </span>
                {lines.length > 0 && (
                  <span className="min-w-[22px] h-[22px] flex items-center justify-center rounded-full bg-[#DDD0F0] text-[#3B0069] text-xs font-sm px-1.5">
                    {lines.length}
                  </span>
                )}
              </div>
              {hasSelectableMaterials && (
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  className="inline-flex items-center justify-center rounded-lg bg-[#3B0069] px-4 py-2 text-sm font-medium text-white hover:bg-[#3B0069D9] transition-colors"
                >
                  Add Material
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3B0069]" />
                <span className="ml-2 text-sm text-gray-500">
                  Loading materials…
                </span>
              </div>
            ) : showPicker ? (
              <MaterialPickerModal
                materials={selectableMaterials}
                alreadyAddedIds={addedIds}
                onConfirm={handlePickerConfirm}
                onClose={() => setShowPicker(false)}
                onRedirectToMaterialLibrary={onRedirectToMaterialLibrary}
              />
            ) : lines.length === 0 ? (
              hasSelectableMaterials ? (
                <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-white hover:border-[#3B0069] hover:bg-[#FAF8FF] transition-colors">
                  <p className="text-sm font-medium text-gray-600">
                    Add materials from your inventory
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Choose one or more items to add to the warehouse
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowPicker(true)}
                    className="mt-4 inline-flex items-center justify-center rounded-lg bg-[#3B0069] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#3B0069D9] transition-colors"
                  >
                    Select Materials
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-xl bg-white">
                  <div className="w-12 h-12 rounded-full bg-[#EDE3F6] flex items-center justify-center mb-3 text-[#3B0069]">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 7h16" />
                      <path d="M6 7l1 12h10l1-12" />
                      <path d="M9 11v5" />
                      <path d="M15 11v5" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-[#232323]">
                    You have already added all your materials
                  </p>
                  <p className="text-xs text-gray-400 mt-1 text-center max-w-sm">
                    Go to your Material Library to create new materials, then return here to add them to the warehouse.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      onClose?.();
                      onRedirectToMaterialLibrary?.();
                    }}
                    className="mt-4 inline-flex items-center justify-center rounded-lg bg-[#3B0069] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#3B0069D9] transition-colors"
                  >
                    Go to Material Library
                  </button>
                </div>
              )
            ) : (
              <div className="space-y-3">
                {lines.map((line, index) => (
                  <div
                    key={line.material.id}
                    className="flex justify-start w-full gap-3"
                  >
                    {/* <button
                      type="button"
                      onClick={() => setShowPicker(true)}
                      className="flex flex-row cursor-pointer items-center w-[42%] justify-between px-4 py-2 rounded-lg bg-[#F3F4F6]"
                    >
                      <span className="text-left text-[#232323] font-normal focus:outline-none overflow-hidden text-ellipsis whitespace-nowrap">
                        {line.material.material_name}
                      </span>
                      <ChevronDown size={16} className="text-gray-600 shrink-0 ml-2" />
                    </button> */}
                    <div className="flex flex-row items-center w-[42%] justify-between px-4 py-2 rounded-lg bg-[#F3F4F6]">
                      <div className="min-w-0">
                        <span className="block text-left text-[#232323] font-normal overflow-hidden text-ellipsis whitespace-nowrap">
                          {line.material.material_name}
                        </span>
                        <span className="block text-xs text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">
                          Brand Name: {line.material.brand_name || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-row items-center w-[48%] justify-between px-3 py-2 rounded-lg bg-[#F3F4F6] gap-2">
                      <input
                        type="number"
                        placeholder="Qty"
                        value={line.currentQuantity}
                        onChange={(e) => updateQty(index, e.target.value)}
                        min="0"
                        step="any"
                        className="w-[50%] text-center bg-transparent text-[#232323] font-normal focus:outline-none"
                      />
                      {line.material.uom && (
                        <>
                          <div className="w-px h-5 bg-gray-300 shrink-0" />
                          <span className="px-2 text-[#232323] shrink-0 whitespace-nowrap font-normal text-sm">
                            {line.material.uom}
                          </span>
                        </>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => removeLine(index)}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-100 bg-white flex items-center justify-center shrink-0">
            <button
              type="button"
              onClick={createWarehouseMaterials}
              disabled={
                submitting ||
                lines.length === 0 ||
                lines.some((l) => !l.currentQuantity)
              }
              className="min-w-[240px] bg-[#3B0069] hover:bg-[#3B0069D9] disabled:cursor-not-allowed text-white font-medium py-2 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-40"
            >
              {submitting ? "Adding..." : `Add Material${lines.length > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateWarehouseMaterial;

